import {loadStripe} from '@stripe/stripe-js';
import Pristine from 'pristinejs';
import * as Sentry from '@sentry/browser';
import Auth, {checkAuthStatusAsync, isAuthenticated} from './auth';
import {DASHBOARD_URL, PAYPAL_CLIENT_ID, AFFILIATLY_ID} from './config';
import {startLoading, finishLoading, startLoadingSpinner, finishLoadingSpinner} from './loading';
import {showToast, hideToast, isToastActive} from './toast';
import {countryCodeCurrency, createXHR, dashboardProductMap, PristineConfig, supportedCurrencies, showOverlay} from './utils';

export const payment = {
  discountCode: null,
  appliedDiscountCode: null,
  clientSecret: null,
  form: null,
  formData: null,
  getSubmit: null,
  gbpPrices: null,
  paypal: {
    button: null,
    createOrder: async () => {
      startLoading(payment.form);
      payment.loading(true);
      const data = payment.fetchPaymentData();
      return new Promise((resolve, reject) => {
        const xhr = createXHR({
          method: 'POST',
          endpoint: '/api/v2/user/paypal-create-order',
          data,
        });

        xhr.onload = () => {
          if (xhr.status !== 200) {
            return reject({ xhr });
          }
          const result = JSON.parse(xhr.responseText);
          if (!result.id) {
            return reject({ xhr });
          }
          payment.paypal.id = result.id;
          return resolve(result.id);
        }
      })
        .catch(({ xhr }) => {
          switch (xhr.status) {
            case 403:
              Sentry.captureMessage('You already own this product');
              return payment.showGlobalError({ response: { error: 'You already own this product' } });
            case 404:
              Sentry.captureMessage('Unknown product');
              payment.showError({ error: 'Unknown product' });
              break;
            case 422:
              Sentry.captureMessage(xhr.responseText);
              payment.showError(JSON.parse(xhr.responseText));
              break;
            default:
              Sentry.captureMessage('We are experiencing an issue communicating with PayPal, please use an alternative payment method.');
              payment.showGlobalError({
                response: {
                  error: 'We are experiencing an issue communicating with PayPal, please use an alternative payment method.'
                },
                persist: true,
              });
              break;
          }
          finishLoading(payment.form);
        });
    },
    id: null,
    instance: null,
    onApprove: async (data, _) => {
      return new Promise((resolve, reject) => {
        const xhr = createXHR({
          method: 'POST',
          endpoint: '/api/v2/user/paypal-capture-order',
          data,
        });

        xhr.onload = () => {
          if (xhr.status !== 200) {
            return reject({ xhr });
          }
          const result = JSON.parse(xhr.responseText);
          if (!result.id) {
            return reject({ xhr });
          }
          return resolve(result);
        }
      })
        .then(() =>
          payment.orderComplete({ withPayment: true }))
        .catch(xhr => {
          Sentry.captureMessage(xhr.responseText);
          payment.showError(JSON.parse(xhr.responseText));
        })
        .finally(() =>
          finishLoading(payment.form))
    },
    onCancel: () => {
      finishLoading(payment.form);
    },
    onError: error => {
      Sentry.captureMessage(error);
      // TODO: display PayPal error to user
      finishLoading(payment.form);
    }
  },
  product: { id: null, price: null, slug: null, type: null },
  stripe: null,
  stripeCard: null,
  stripeElements: null,
  stripePaymentRequest: null,
  stripePaymentRequestBtn: null,
  initPayPal: () => {
    if (payment.paypal.button === null) {
      payment.paypal.button = paypal.Buttons({
        createOrder: payment.paypal.createOrder,
        fundingSource: paypal.FUNDING.PAYPAL,
        onApprove: payment.paypal.onApprove,
        onCancel: payment.paypal.onCancel,
        onError: payment.paypal.onError,
        style: PayPalBtnStyle,
      });
    }
    if (payment.form === null) {
      // form not yet setup
      return;
    }
    // const btnLocation = document.querySelector('.js-paypal-btn-element[data-form="purchase-form"]');
    const btnLocation = document.querySelector('.js-paypal-btn-element[data-form="purchase-subscription-form"]');
    if (btnLocation.length === 0 || btnLocation.children.length > 0) {
      // btn location not available or btn location already has a rendered paypal btn
      return;
    }
    if (!payment.paypal.button.isEligible()) {
      // paypal not able to render
      return;
    }
    payment.paypal.button.render(btnLocation);
  },
  setupForm: async (form) => {
    try {
      // TODO: add prefill
      payment.form = form;
      payment.formData = new FormData(form);
      payment.getSubmit = document.getElementById('get-product-submit');
      payment.purchaseSubmit = document.getElementById('purchase-product-submit');
      // STRIPE
      const stripePublishableKey = payment.formData.get('stripe_publishable_key');
      payment.stripe = await loadStripe(stripePublishableKey);
      // disable submit button during setup
      payment.purchaseSubmit.disabled = true;
      payment.getPaymentIntent();
      payment.createPaymentRequest();

      // PayPal
      // payment.initPayPal();

      if (payment.getSubmit) {
        payment.getSubmit.addEventListener('click', event => {
          event.preventDefault();
          payment.getFreeProduct();
        });
      }
    }
    catch (error) {
      console.log("setupForm error", error);
    }
  },
  installationDiscount: discount => {
    payment.discountCode = discount;
  },
  fetchPaymentData: discountCodeApplied => {
    payment.product.id = parseInt(payment.formData.get('product_id'));
    //payment.product.price = payment.formData.get('product_price');
    payment.product.slug = payment.formData.get('product_slug');
    payment.product.type = payment.formData.get('product_type');
    const currency = window.localStorage.getItem('currency');
    let data = {
      currency,
      product_id: payment.product.id,
      //product_price: payment.product.price,
      product_slug: payment.product.slug,
      product_type: payment.product.type,
    };

    if (discountCodeApplied) {
      const discount_code = discount?.formData?.get('discount_code');
      if (discount_code !== '') {
        payment.appliedDiscountCode = discount_code;
        data = { discount_code, ...data };
      }
    } else if (payment.appliedDiscountCode) {
      data = { discount_code: payment.appliedDiscountCode, ...data };
    }
    return data;
  },
  getPaymentIntent: appliedDiscountCode => {
    const data = payment.fetchPaymentData(appliedDiscountCode);
    // Construct an HTTP request
    const xhr = createXHR({
      method: 'POST',
      endpoint: '/api/v2/user/create-payment-intent',
      data,
    });
    xhr.onload = () => {
      let error = null;
      switch (xhr.status) {
        case 200:
          const result = JSON.parse(xhr.responseText);
          if (!!result.clientSecret) {
            payment.clientSecret = result.clientSecret;
          }
          if (payment.stripeElements === null && !result.isFree) {
            payment.loadStripeElements();
          }

          break;
        case 403:
          error = 'You already own this product';
          Sentry.captureMessage(error);
          return payment.showGlobalError({ response: { error } });
        // case 404:
        //   error = 'Unknown product';
        //   Sentry.captureMessage(error);
        //   return payment.showError({ error });
        case 422:
          error = xhr.responseText;
          Sentry.captureMessage(error);
          return payment.showError(JSON.parse(error));
        default:
          Sentry.captureMessage('Unhandled status');
          break;
      }
    }
  },
  getSubscriptionRelevance: async () => {
    let result, data;
    if (!!payment.discountCode) {
      data = {};
    }
    else {
      data = payment.fetchPaymentData();
    }
    return new Promise((resolve, reject) => {
      const xhr = createXHR({
        method: 'POST',
        endpoint: '/api/v2/course/start-course',
        data,
      });
      xhr.onload = () => {
        if (xhr.status !== 200) {
          return reject({ xhr });
        }
        result = JSON.parse(xhr.responseText);
        if (!result) {
          return reject({ xhr });
        }
        return resolve(result);
      }
    })
  },
  getStudentsCount: async () => {
    let result;
    // NOTE: mock
    // return { count: 102032 };
    return new Promise((resolve, reject) => {
      const xhr = createXHR({
        method: 'GET',
        endpoint: '/api/v2/user/all_students_count',
      });
      xhr.onload = () => {
        if (xhr.status !== 200) {
          return reject({ xhr });
        }
        result = JSON.parse(xhr.responseText);
        if (!result) {
          return reject({ xhr });
        }
        return resolve(result);
      }
    })
  },
  deleteDiscountCode: async () => {
    return new Promise((resolve, reject) => {
      // NOTE: mock
      // return resolve();
      const xhr = createXHR({
        method: 'DELETE',
        endpoint: '/api/v2/user/delete-redeem-code',
      });
      xhr.onload = () => {
        if (xhr.status !== 204) {
          return reject({ xhr });
        }
        return resolve();
      }
    })
  },
  activateGiftCode: async () => {
    let result;
    Sentry.captureMessage("good");
    return new Promise((resolve, reject) => {
      const xhr = createXHR({
        method: 'POST',
        endpoint: '/api/v2/user/activate-gift-code',
      });

      xhr.onload = () => {
        let error = null;
        switch (xhr.status) {
          case 200:
            result = JSON.parse(xhr.responseText);
            break;

          case 400:
            error = 'Unknown product';
            Sentry.captureMessage(error);
            return reject({ xhr })
          default:
            Sentry.captureMessage('Unhandled status');
            break;
        }
        return resolve(result);
      }
    })
  },
  getSubscriptionsStatus: async () => {
    let result;
    return new Promise((resolve, reject) => {
      const xhr = createXHR({
        method: 'GET',
        endpoint: '/api/v2/user/status',
      });

      xhr.onload = () => {
        if (xhr.status !== 200) {
          return reject({ xhr });
        }
        result = JSON.parse(xhr.responseText);
        if (!result) {
          return reject({ xhr });
        }
        return resolve(result);
      }
    })
  },
  getPaymentLink: async () => {
    let result;
    return new Promise((resolve, reject) => {
      const xhr = createXHR({
        method: 'GET',
        endpoint: '/api/v2/user/latest_subscription_payment_link',
      });

      xhr.onload = () => {
        if (xhr.status !== 200) {
          return reject({ xhr });
        }
        result = JSON.parse(xhr.responseText);
        if (!result) {
          return reject({ xhr });
        }
        return resolve(result);
      }
    })
  },
  getProductsWithSubscriptions: async () => {
    let result;
    return new Promise((resolve, reject) => {
      const xhr = createXHR({
        method: 'GET',
        endpoint: '/api/v2/user/get-prices-with-subscriptions',
      });

      xhr.onload = () => {
        if (xhr.status !== 200) {
          return reject({ xhr });
        }
        result = JSON.parse(xhr.responseText);
        if (!result) {
          return reject({ xhr });
        }
        return resolve(result);
      }
    })
  },
  createSubscription: async (priceId, cardTokenId) => {
    let productData;
    let result, data;
    //startLoading(payment.form);
    //payment.loading(true);
    if (!!payment.discountCode) {
      productData = {};
      data = { "product_type": "site", "price_id": priceId, "product_id": 7556, "card_token": cardTokenId };
    }
    else {
      productData = payment.fetchPaymentData();
      data = { "product_type": "site", "price_id": priceId, "product_id": productData.product_id, "card_token": cardTokenId };
    }
    const selectedCurrency = window.localStorage.getItem('currency');
    if (selectedCurrency) {
      data = {...data, currency: selectedCurrency.toLowerCase()};
    }

    return new Promise((resolve, reject) => {


      const xhr = createXHR({
        method: 'POST',
        endpoint: '/api/v2/user/create-subscription',
        data,
      });
      xhr.onload = () => {
        if (xhr.status !== 200) {
          return reject({ xhr });
        }
        finishLoading(payment.form)
        result = JSON.parse(xhr.responseText);
        if (!result) {
          return reject({ xhr });
        }
        if (!!result.clientSecret) {
          payment.clientSecret = result.clientSecret;
        }
        if (payment.stripeElements === null && !result.isFree) {
          payment.loadStripeElements();
        }
        return resolve(result);
      }
    })
  },
  redeemACode: async () => {
    let result;
    //startLoading(payment.form);
    // payment.loading(true);
    let data = {
      code: payment.discountCode
    }
    return new Promise((resolve, reject) => {
      const xhr = createXHR({
        method: 'POST',
        endpoint: '/api/v2/user/redeem-gift-code',
        data,
      });
      xhr.onload = () => {
        if (xhr.status !== 200) {
          return reject(JSON.parse(xhr.responseText));
        }
        finishLoading(payment.form)
        result = JSON.parse(xhr.responseText);
        if (!result) {
          return reject({ xhr });
        }
        return resolve(result);
      }
    })
  },
  markPurchase: async (orderId, price) => {
    const cookieValue = getCookieValue();
    console.log('val', cookieValue)
    if (!cookieValue) return;

    const parsedCookie = parseCookieString(cookieValue);
    const { id_user, id_token, aff_uid } = parsedCookie;
    console.log(id_user, id_token, aff_uid);

    const params = new URLSearchParams();
    params.append('id_affiliatly', AFFILIATLY_ID);
    params.append('id_user', id_user);
    params.append('id_hash', id_token);
    params.append('aff_uid', aff_uid);
    params.append('price', price);
    params.append('order', orderId);
    params.append('mode', 'mark');
    console.log('params', params)

    // Step 2: Send the POST request with Fetch API
    return fetch('https://www.affiliatly.com/api_request.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: params.toString()
    });
    

    // const affiliatlyV3 = getCookieValue('affiliatly_v3');
    // const parsedCookies = parseCookie(affiliatlyV3);
    // console.log(affiliatlyV3, parsedCookies)
    // if (!parsedCookies) return;

    // const token = parsedCookies.id_token;
    // const user = parsedCookies.id_user;
    // const affId = parsedCookies.aff_uid;
    // console.log(token, user, affId);
    return;

    return new Promise((resolve, reject) => {
      const xhr = createXHR({
        method: 'POST',
        endpoint: '/api/v2/user/redeem-gift-code',
        data,
      });
      xhr.onload = () => {
        if (xhr.status !== 200) {
          return reject(JSON.parse(xhr.responseText));
        }
        finishLoading(payment.form)
        result = JSON.parse(xhr.responseText);
        if (!result) {
          return reject({ xhr });
        }
        return resolve(result);
      }
    })
  },
  createPaymentRequest: appliedDiscountCode => {

    const country = window.localStorage.getItem('countryCode') || 'US';

    const data = payment.fetchPaymentData(appliedDiscountCode);
    try {
      payment.stripePaymentRequest = payment.stripe.paymentRequest({
        country,
        currency: data.currency.toLowerCase(),
        total: {
          label: data.product_slug,
          amount: 1, // Stripe works in cents
        },
        requestPayerName: false,
        requestPayerEmail: false,
      });
    }
    catch (error) {
      console.log("error createPaymentRequest", error);
    }
  },
  getFreeProduct: () => {
    const data = payment.fetchPaymentData();
    startLoading(payment.form);

    // Construct an HTTP request
    const xhr = createXHR({
      method: 'POST',
      endpoint: '/api/v2/user/get-free-product',
      data,
    });
    xhr.onload = () => {
      switch (xhr.status) {
        case 200:
          payment.orderComplete({ withPayment: true });
          break;
        default:
          finishLoading(payment.form);
          let error = xhr.responseText;
          Sentry.captureMessage(error);
          return payment.showError(JSON.parse(error));
      }
    }
  },
  loading: (isLoading) => {
    if (isLoading) {
      document.querySelectorAll('.js-transaction-in-progress').forEach(el => el.classList.remove('is-hidden'));
      payment.purchaseSubmit.disabled = true;
      payment.getSubmit.disabled = true;
      // payment.form.querySelector('.js-purchase-form-spinner').classList.remove('is-hidden');
    } else {
      document.querySelectorAll('.js-transaction-in-progress').forEach(el => el.classList.add('is-hidden'));
      payment.purchaseSubmit.disabled = false;
      payment.getSubmit.disabled = false;
      // payment.form.querySelector('.js-purchase-form-spinner').classList.add('is-hidden');
    }
  },
  loadStripeElements: () => {
    try {
      payment.stripeElements = payment.stripe.elements();
      const style = {
        base: {
          color: '#32325d',
          fontFamily: 'Arial, sans-serif',
          fontSmoothing: "antialiased",
          fontSize: "16px",
          "::placeholder": {
            color: "#32325d"
          },
          padding: '2px',
        },
        invalid: {
          fontFamily: 'Arial, sans-serif',
          color: "#fa755a",
          iconColor: "#fa755a"
        }
      };

      // Stripe: Credit Card
      payment.stripeCard = payment.stripeElements.create('card', { style });
      // Stripe injects an iframe into the DOM for Credit Card Payments
      payment.stripeCard.mount(payment.form.querySelector('.js-card-element'));
      payment.stripeCard.on('change', payment.manageFormOnChange);

      // Stripe: Payment Request
      payment.stripePaymentRequestBtn = payment.stripeElements.create('paymentRequestButton', {
        paymentRequest: payment.stripePaymentRequest
      });
      // Stripe injects an iframe into the DOM for Apple Pay
      payment.stripePaymentRequest.canMakePayment().then(result => {
        const reqBtnEl = document.querySelector('.js-payment-request-btn-element[data-form="purchase-form"]')
        if (result) {
          payment.stripePaymentRequestBtn.mount(reqBtnEl);
        } else {
          reqBtnEl.classList.add('is-hidden');
        }
      });

      payment.stripePaymentRequest.on('paymentmethod', payment.paymentMethodHook);

      payment.form.addEventListener('submit', payment.submitForm);
    }
    catch (err) {
      console.log("loadStripeElements error", err);
    }
  },
  manageFormOnChange: event => {
    // Disable the Pay button if there are no card details in the Element
    payment.purchaseSubmit.disabled = event.empty;
    payment.purchaseSubmit.disabled = !selectedPlan;
    // payment.form.querySelector('.js-purchase-form-error').textContent = event.error ? event.error.message : '';
  },
  paymentMethodHook: ev => {
    // Confirm the PaymentIntent without handling potential next actions (yet).
    payment.stripe.confirmCardPayment(
      payment.clientSecret,
      { payment_method: ev.paymentMethod.id },
      { handleActions: false }
    ).then(({ paymentIntent, error }) => {
      if (error) {
        // Report to the browser that the payment failed, prompting it to
        // re-show the payment interface, or show an error message and close
        // the payment interface.
        ev.complete('fail');
        throw { error };
      }

      // Report to the browser that the confirmation was successful, prompting
      // it to close the browser payment method collection interface.
      ev.complete('success');
      // Check if the PaymentIntent requires any actions and if so let Stripe.js
      // handle the flow. If using an api version older than "2019-02-11" instead
      // instead check for: `paymentIntent.status === "requires_source_action"`.
      if (paymentIntent.status === "requires_action") {
        // Let Stripe.js handle the rest of the payment flow.
        return payment.stripe.confirmCardPayment(clientSecret);
      }
      return Promise.resolve({ paymentIntent });
    }).then(({ paymentIntent, error }) => {
      if (error) {
        throw { error };
      }
      // The payment succeeded!
      return;
    }).then(() =>
      payment.orderComplete({ withPayment: true }))
      .catch(({ error }) => {
        Sentry.captureException(error);
        payment.showError(error.message);
      })
      .finally(() =>
        finishLoading(payment.form));
  },
  orderComplete: withPayment => {
    //window.localStorage.removeItem('discount_code');
    //document.querySelectorAll('.js-transaction-in-progress').forEach(el =>
    //el.classList.add('is-hidden'));
    // TODO:
    // payment.markPurchase();
    if (withPayment) {
      document.querySelectorAll('.js-payment-success').forEach(el =>
        el.classList.remove('is-hidden'));
    }
    openConfirm();
    // console.log(window.markPurchase);
    // if (window.markPurchase) {
    //   window.markPurchase(AFFILIATLY_ID);
    //   // Affiliatly.markPurchase(
    //   //   'AF-105152',
    //   //   paymentIntent.id,
    //   //   purchaseDetails.amount
    //   // );
    //   // markPurchase(AFFILIATLY_ID, id, convertedAmount, appliedDiscountCode);
    // }
    // var affi_id_order = '##YOUR-ORDER-ID##';
    // var affi_order_price = '##YOUR-ORDER-PRICE##';
    // var affi_order_coupons = '##YOUR-ORDER-USED-COUPON##';
    // var url_affi = "https://static.affiliatly.com/v3/affiliatly.js?affiliatly_code=AF-105152&conversion=1&id_order="+ encodeURIComponent( affi_id_order ) + "&order_price="+ affi_order_price +"&order_coupons=" + affi_order_coupons;
    // var script_affi = document.createElement("script");
    // script_affi.type = "text/javascript";
    // script_affi.src = url_affi;
    // document.getElementsByTagName("head")[0].appendChild(script_affi);

    dashboardRedirect = `${DASHBOARD_URL}/${dashboardProductMap[payment.product.type]}/${payment.product.slug}`;
    payment.getSubscriptionRelevance().then(() => { })
    setTimeout(() => {
      // send user to product in the dashboard
      finishLoading(payment.form)
      payment.purchaseSubmit.disabled = false;
      window.location.href = dashboardRedirect;
    }, 10000);
  },
  payWithApplePay: event => {
    startLoading(payment.form);
    payment.loading(true);
    payment.stripe
      .confirmCardPayment(
        payment.clientSecret,
        { payment_method: event.paymentMethod.id },
        { handleActions: false },
      )
      .then(({ paymentIntent, error }) => {
        if (error) {
          throw { error };
        }

        if (paymentIntent.status === "requires_action") {
          // Let Stripe.js handle the rest of the payment flow.
          return payment.stripe.confirmCardPayment(clientSecret);
        }
        return Promise.resolve({ paymentIntent });
      })
      .then(({ paymentIntent, error }) => {
        if (error) {
          throw { error };
        }
        // The payment succeeded!
        return;
      })
      .then(() =>
        payment.orderComplete({ success: true }))
      .catch(({ error }) => {
        Sentry.captureException(error);
        payment.showError(error.message);
      })
      .finally(() =>
        finishLoading(payment.form))
  },
  confirmCardSetup: async (clientSecret) => {
    startLoading(payment.form);
    console.log('ConfirmCardSetup');
    // payment.loading(true);

    payment.stripe
      .confirmCardSetup(clientSecret)
      .then(result => {
        const orderId = result.setupIntent.id;
        const price = (0).toFixed(2);
        payment.markPurchase(orderId, price);
        // if (window.markPurchase) {

        //   window.markPurchase(AFFILIATLY_ID, orderId, price);
        // }

        if (result.error) {
          console.log('result error =>', result.error)
          throw result.error;
        }
        // The setup succeeded!
        console.log('The setup succeeded!')
        return;
      })
      .then(() => {
        console.log("orderComplete");
        return payment.orderComplete({ withPayment: true })
      })
      .catch(error => {
        Sentry.captureException(error);
        payment.showError(error.message);
        console.log("Error: " + error.message);
        finishLoading(payment.form)
        payment.purchaseSubmit.disabled = false;
      })
  },
  payWithCard: async (card, clientSecret) => {
    startLoading(payment.form);
    console.log('PayWithCard');

    const options = {}
    if (card !== null) {
      options["payment_method"] = { card }
    }

    payment.stripe
      .confirmCardPayment(clientSecret, options)
      .then(result => {
        const orderId = result.paymentIntent.id;
        // const price = (result.paymentIntent.amount / 100).toFixed(2);
        if (payment.gbpPrices) {
          const gbpPrice = (payment.gbpPrices.unitAmount / 100).toFixed(2);
          payment.markPurchase(orderId, gbpPrice);
        }
        // if (window.markPurchase) {
        //   // window.markPurchase(AFFILIATLY_ID, orderId, price);
        // }

        if (result.error) {
          throw result.error;
        }
        // The payment succeeded!
        console.log('The payment succeeded!');
        return;
      })
      .then(() => {
        console.log("orderComplete");
        return payment.orderComplete({ withPayment: true })
      })
      .catch(error => {
        Sentry.captureException(error);
        payment.showError(error.message);
        console.log("Error: " + error.message);
        finishLoading(payment.form)
        payment.purchaseSubmit.disabled = false;
      })
      .finally(() => {
        console.log("finally");
        let getIsPaid;
        payment.getPaymentLink().then((result) => {//need start before payment
          console.log('getPaymentLink:');
          if (!result.is_paid) {
            window.open(result.payment_link);
            const makeApiCall = () => {
              payment.getPaymentLink().then((result) => {
                if (result.is_paid) {
                  window.clearTimeout(getIsPaid);
                  // console.log('activateGiftCode');
                  // payment.activateGiftCode().then((res) => {
                  // }).catch((err) => {
                  //   console.log("activateGiftCode err", err);
                  // })
                }
              })
            }
            // Initiate call
            getIsPaid = setTimeout(makeApiCall(), 5000);
            setTimeout(window.clearTimeout(getIsPaid), 120000);
          }
          else {
            // window.location.href = `${DASHBOARD_URL}`;
            // payment.activateGiftCode().then((res) => {
            //   window.location.href = `${DASHBOARD_URL}`;
            // }).catch((err) => {
            //   console.log("activateGiftCode err", err);
            // })
            // setTimeout(() => {
            //   // send user to product in the dashboard
            //   finishLoading(payment.form)
            //   payment.purchaseSubmit.disabled = false;
            //   window.location.href = `${DASHBOARD_URL}/${dashboardProductMap[payment.product.type]}/${payment.product.slug}`;
            // }, 1000)
          }
        })
      });
  },
  showError: response => {
    payment.loading(false);
    if (response.hasOwnProperty('error')) {
      const errorMsg = payment.form.querySelector('.js-purchase-form-error');
      errorMsg.textContent = response.error;
      setTimeout(() => {
        errorMsg.textContent = "";
      }, 4000);
    }
  },
  showGlobalError: ({ response, persist }) => {
    const globalErrorEl = document.getElementById('purchase-form-global-error');
    globalErrorEl.classList.remove('is-hidden');
    globalErrorEl.textContent = response.error;
    if (persist) {
      return;
    }
    setTimeout(() =>
      document.querySelectorAll('.o-overlay').forEach(el => el.classList.add('is-hidden')), 4000);
  },
  submitForm: event => {
    NOTE:
    event.preventDefault();
    console.log('submitForm');

    startLoading(payment.form);
    payment.purchaseSubmit.disabled = true;

    const formType = payment.purchaseSubmit.getAttribute("form");

    if (formType === 'purchase-subscription-form') {
      //Complete payment when the submit button is clicked
      payment.stripe.createToken(payment.stripeCard).then(res => {
        if (res.error) {
          showToast(res.error.message, "warn");
          finishLoading(payment.form);
          payment.purchaseSubmit.disabled = false;
          return;
        }
        const cardTokenId = res.token.id

        payment.createSubscription(payment.priceId, cardTokenId).then(res => {
          if (parserSecretId(res.clientSecret) !== "pi") {
            payment.confirmCardSetup(payment.clientSecret);
          } else {
            payment.payWithCard(null, payment.clientSecret);
          }
        })
        .catch(() => {
          finishLoading(payment.form);
          payment.purchaseSubmit.disabled = false;

          showToast("Your card was declined", "error");
        })
      }).catch(() => {
        finishLoading(payment.form);
        payment.purchaseSubmit.disabled = false;

        showToast("Something went wrong", "error");
      })
    } else {
      payment.payWithCard(payment.stripeCard, payment.clientSecret);
    }
  }
};

const discount = {
  form: null,
  formData: null,
  pristine: null,

  setupForm: form => {
    discount.form = form;
    discount.formData = new FormData(form);
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('discount_code')) {
      const discount_code = urlParams.get('discount_code')
      window.localStorage.setItem('discount_code', discount_code);
      form.querySelector('input[name="discount_code"]').value = discount_code;
      discount.applyDiscount();
    }

    // Pre-validate discount codes using AJAX
    form.addEventListener('submit', event => {
      event.preventDefault();
      discount.applyDiscount();
    });
  },

  applyDiscount: () => {
    // discount.form.querySelector('.js-discount-spinner').classList.add('is-hidden');
    discount.form.querySelector('.js-discount-correct').classList.add('is-hidden');

    const pristine = new Pristine(discount.form, PristineConfig);

    if (pristine.validate()) {
      discount.preValidate(pristine);
    }
  },

  preValidate: (pristine) => {
    const formData = new FormData(discount.form);
    discount.formData = formData;
    const params = new URLSearchParams(formData).toString();

    startLoading(discount.form);

    // Construct an HTTP request
    const xhr = createXHR({
      method: 'GET',
      endpoint: `/api/v2/discount-code/validate?${params}`,
    });

    xhr.onload = () => {
      finishLoading(discount.form);
      switch (xhr.status) {
        case 200:
          discount.validationSuccess(JSON.parse(xhr.responseText))
          break;
        case 422:
          discount.parseErrors(JSON.parse(xhr.responseText), pristine);
          break;
        default:
          Sentry.captureMessage(`unknown status = ${xhr.status}`);
          break;
      }
    }
  },

  // Parse server errors and display with relevant input field
  parseErrors: (data, pristine) => {
    if (data.hasOwnProperty('error') && data.error.hasOwnProperty('discount')) {
      const input = discount.form.querySelector('input[name="discount_code"]');
      pristine.addError(input, data.error.discount);
    }
    discount.form.querySelector('button[type="submit"]').disabled = false;
  },

  validationSuccess: response => {
    discount.show(response);
    if (discount.formData.has('is_gift')) {
      gift.getPaymentIntent(true);
    } else {
      payment.getPaymentIntent(true);
    }
  },

  show: response => {
    const currency = window.localStorage.getItem('currency');

    if (response.data.new_prices[currency] === 0) {
      document.querySelectorAll('.js-price').forEach(el => {
        el.dataset.eur = 'FREE';
        el.dataset.gbp = 'FREE';
        el.dataset.usd = 'FREE';
        el.textContent = 'FREE';
        el.classList.add('c-price--free');
      });
      document.querySelectorAll('.js-price-input').forEach(el => {
        el.dataset.eur = 0;
        el.dataset.gbp = 0;
        el.dataset.usd = 0;
        el.value = 0;
      });
    } else {
      document.querySelectorAll('.js-price').forEach(el => {
        el.dataset.eur = response.data.new_prices.EUR;
        el.dataset.gbp = response.data.new_prices.GBP;
        el.dataset.usd = response.data.new_prices.USD;
        el.textContent = response.data.new_prices[currency];
        el.classList.remove('c-price--free');
      });
      document.querySelectorAll('.js-price-input').forEach(el => {
        el.dataset.eur = response.data.new_prices.EUR;
        el.dataset.gbp = response.data.new_prices.GBP;
        el.dataset.usd = response.data.new_prices.USD;
        el.value = response.data.new_prices[currency];
      });
    }

    // discount.form.querySelector('.js-discount-spinner').classList.add('is-hidden');
    discount.form.querySelector('.js-discount-correct').classList.remove('is-hidden');

    discount.convertForm(response.data.new_prices[currency] === 0);
  },
  convertForm: isFree => {
    let cardEl;
    let paymentFormSubmitEl
    let getFormSubmitEl;

    if (discount.formData.has('is_gift')) {
      cardEl = gift.purchaseForm.querySelector('.js-card-element');
      paymentFormSubmitEl = gift.purchaseSubmit;
      getFormSubmitEl = gift.getSubmit;
    } else {
      cardEl = payment.form.querySelector('.js-card-element');
      paymentFormSubmitEl = payment.purchaseSubmit;
      getFormSubmitEl = payment.getSubmit;
    }
    if (isFree) {
      cardEl.parentNode.classList.add('is-hidden');
      paymentFormSubmitEl.classList.add('is-hidden');
      paymentFormSubmitEl.setAttribute('disabled', true);
      getFormSubmitEl.classList.remove('is-hidden');
      getFormSubmitEl.removeAttribute('disabled');
    } else {
      cardEl.parentNode.classList.remove('is-hidden');
      paymentFormSubmitEl.classList.remove('is-hidden');
      paymentFormSubmitEl.removeAttribute('disabled');
      getFormSubmitEl.classList.add('is-hidden');
      getFormSubmitEl.setAttribute('disabled', true);
    }
  }
};

// Function to retrieve the value of a specific cookie by key
function getCookieValue() {
  const cookieString = document.cookie;
  const cookies = cookieString.split('; ');

  const affiliatlyCookieString = cookies.find(cookie => cookie.startsWith('affiliatly_v3='));

  if (affiliatlyCookieString) {
    const valueWithoutPrefix = affiliatlyCookieString.replace('affiliatly_v3=', '');

    // console.log(decodeURIComponent(valueWithoutPrefix))
    return decodeURIComponent(valueWithoutPrefix);
    // return valueWithoutPrefix;
  }
  return null;
}

// Function to parse a cookie string into an object
function parseCookieString(cookieString) {
  const cookieObject = {};
  const cookiesArray = cookieString.split('&');
  cookiesArray.forEach(cookie => {
    const [key, value] = cookie.split('=');
    cookieObject[key] = value;
  });
  return cookieObject;
}

let course = null;
let selectedPlan = null;
let dashboardRedirect = null;
const months = [
  "January", "February", "March",
  "April", "May", "June",
  "July", "August", "September",
  "October", "November", "December"
];

// TODO: use try catch
const init = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('aff')) {
    localStorage.setItem('aff', aff);
  }

  const path = '/checkout';
  if (!window.location.pathname.includes(path)) {
    return;
  }

  renderStudentsCount(0);
  renderConfirm();
  const isAuth = await checkAuthStatusAsync();
  if (!isAuth) {
    window.location.href = '/';
  }

  const params = new URLSearchParams(window.location.search);
  const prodId = params.get("product_id");
  const type = params.get("type");

  if (type !== null && type !== 'course') {
    window.location.href = '/';
  }

  configureCurrency();

  // Setup currency
  const currencySelectorEl = document.getElementById('currency-selector');
  if (currencySelectorEl) {
    let currencySign = '$';
    const selectedCurrency = window.localStorage.getItem('currency');

    switch (selectedCurrency.toLowerCase()) {
      case "usd":
        currencySign = '$';
        break;
      case "gbp":
        currencySign = "£"
        break;
      case "eur":
        currencySign = "€"
        break;
      default:
        currencySign = ""
        break;
    }

    document.querySelectorAll('span.currency').forEach(span => span.innerHTML = currencySign);

    document.querySelectorAll('#currency-selector').forEach(selector => {
      selector.addEventListener('change', (event) => {
        const selectedCurrency = event.currentTarget.value;
        window.localStorage.setItem('currency', selectedCurrency);
        injectCurrency(selectedCurrency);
  
        loadPlans(selectedCurrency);
      })
    })
  }

  // NOTE: Step 1
  const coursesDataEl = document.getElementById('courses-data');
  if (!coursesDataEl) {
    window.location.href = '/';
  }
  const courses = JSON.parse(coursesDataEl.innerHTML);
  if (courses === null) return;

  if (prodId !== null) {
    course = courses.find((course) => course.id === prodId);
  }
  
  if (course) {
    course = {...course.blob, type};
    const {image_url, slug, title, artist} = course;
    const card = document.querySelector('.js-click-on-card-from-selection-subscription-form');
    card.querySelector('.js-card-img').setAttribute('src', image_url);
    // const cardSlug = card.querySelector('.js-card-slug');
    // cardSlug.setAttribute('href', `/courses/${slug}`);
    // cardSlug.innerText = title ?? 'Best source of quality music instruction and resources available online - customer support team is really helpful too.';
    // if (artist.display_name) {
    //   card.querySelector('.t-caption').innerHTML = `with <span class="js-card-guru">${artist.display_name}</span>`
    // }
    document.querySelector('.course-title').innerText = title;
    document.querySelector('.course-title').parentElement.style.display = 'list-item';
    card.classList.remove('hidden');
  } else {
    // NOTE: no course
    const defaultBanner = document.querySelector('.options-information-rules-and-preview__preview-default');
    defaultBanner.classList.remove('hidden');
  }

  // TODO: Step 2
  let isSubscribed = false;
  let isStarted = false;
  let isPastDue = false;
  const currency = window.localStorage.getItem('currency');

  // if (!isAuthenticated) {
  //   // TODO: check this
  //   console.log('NOT AUTH');
  //   // if user is not authenticated, start sign up flow first
  //   Auth.openSignUpOverlay(null, {flow: 'authenticateSelection'});
  //   return;
  // }

  // TODO:
  renderTermsOfService(currency);
  loadPlans(currency);
  initialDataPurchaseSubscription();
  payment.getStudentsCount().then(value => {
    renderStudentsCount(value.count);
  });
  const res = await payment.getSubscriptionRelevance();

  isSubscribed = res.is_subscribed;
  isStarted = res.is_started;
  isPastDue = res.is_past_due;

  if (isSubscribed) {
    window.location.href = `${DASHBOARD_URL}/${dashboardProductMap[payment.product.type]}/${payment.product.slug}`;
  }
  else {
    if (isStarted) {
      window.location.href = `${DASHBOARD_URL}`;
    }
    if (isPastDue) {
      window.location.href = `${DASHBOARD_URL}/student/settings`;
    }
  }

  document.querySelectorAll('.discount-checkout-btn').forEach(el => el.addEventListener('click', applyDiscount));
};

function renderTermsOfService(currency) {
  let currencySign = "";
  switch (currency.toLowerCase()) {
    case "usd":
      currencySign = '$';
      break;
    case "gbp":
      currencySign = "£"
      break;
    case "eur":
      currencySign = "€"
      break;
    default:
      currencySign = ""
      break;
  }

  const todayDate = new Date();
  const termsOfService = document.querySelector('.purchase-subscription__terms-of-service');
  termsOfService.innerHTML = `<strong>By clicking "Place Secure Order", you agree to adhere to our <a href="/terms" class="link-decoration">Terms of Service</a> and authorize a recurring monthly charge of ${currencySign}0.00</strong> that will be automatically applied to your payment method and start on ${getDayWithOrdinal(todayDate.getUTCDate())} ${months[todayDate.getUTCMonth()]} ${todayDate.getUTCFullYear()}. All amounts paid are non-refundable, and rates for subsequent billing periods may change. Cancel your subscription at any time in user settings.`
}

function getDayWithOrdinal(day) {
  if (day >= 11 && day <= 13) {
    return day + 'th';
  }
  
  switch (day % 10) {
    case 1:
        return day + 'st';
    case 2:
        return day + 'nd';
    case 3:
        return day + 'rd';
    default:
        return day + 'th';
  }
}

function renderConfirm() {
  const overlay = document.querySelector('.c-confirm-overlay');
  const closeOverlay = (e) => {
    e.stopPropagation();
    overlay.classList.remove('shown');
    document.body.style.overflowY = 'auto';

    if (dashboardRedirect !== null) {
      window.location.href = dashboardRedirect;
    }
  }

  overlay.addEventListener('click', closeOverlay);
  document.querySelector('.c-confirm-overlay__container').addEventListener('click', (e) => e.stopPropagation());
  overlay.querySelector('.c-confirm-overlay__cta').addEventListener('click', closeOverlay);
}

function openConfirm() {
  const overlay = document.querySelector('.c-confirm-overlay');
  overlay.classList.add('shown');
  document.body.style.overflowY = 'hidden';
}

function renderStudentsCount(count) {
  const container = document.querySelector(".community-block__members-container");
  let numbers = "";

  if (count === 0) {
    for (let i = 0; i < 5; i++) {
      numbers += `
      <div class="community-block__member">
        <span class="number">${0}</span>
        <img class="frame" src="/assets/img/number-frame.png" alt="Number frame">
      </div>
      `;
    }
  } else {
    const digits = count.toString().split("");
    digits.forEach(digit => {
      numbers += `
      <div class="community-block__member">
        <span class="number">${digit}</span>
        <img class="frame" src="/assets/img/number-frame.png" alt="Number frame">
      </div>
      `;
    })
  }

  container.innerHTML = numbers;
}

const loadPlans = async (currency) => {
  var container = document.getElementById("options-subscription-types-wrapper");
  
  container.innerHTML = "";
  startLoadingSpinner(container);
  let res = await payment.getProductsWithSubscriptions();
  finishLoadingSpinner(container);
  let giftCode = null;
  // NOTE: mock
  if (res && res.length > 1) {
    if (res.length === 4) {
      res = res.slice(1);
    }

    if (selectedPlan === null) {
      selectedPlan = {
        id: res[res.length - 1].id
      }
    }
  }
  // NOTE: Reorder items plans
  res = res.reverse();

  res.forEach((i) => {
    // NOTE: mock
    // i.discount_expires_in = "2024-06-22 15:33:08";
    if (i.gift_code) {
      giftCode = i.gift_code;
      payment.discountCode = giftCode
    }

    let trial = i?.recurring?.trial_period_days;
    let count = i?.recurring?.interval_count;
    if (i?.recurring?.interval == 'year') {
      count = 12;
      i.recurring.interval = 'annual';
    }
    let freeTrialInDays = i?.free_trial_in_days;
    let freeTrialInDaysForButtons = i?.free_trial_in_days ? `${i?.free_trial_in_days} days FREE TRIAL,then` : "";
    if (!!trial) {
      trial = `Free trial ${trial} days`;
    }
    else {
      trial = ``;
    }
    switch (currency.toLowerCase()) {
      case "usd":
        i.unit_amount = i.currency_options['usd'].unit_amount;
        i.unit_amount_old = i.currency_options['usd'].unit_amount_old;
        i.currency = "$"
        break;
      case "gbp":
        i.unit_amount = i.currency_options['gbp'].unit_amount;
        i.unit_amount_old = i.currency_options['gbp'].unit_amount_old;
        i.currency = "£"
        break;
      case "eur":
        i.unit_amount = i.currency_options['eur'].unit_amount;
        i.unit_amount_old = i.currency_options['eur'].unit_amount_old;
        i.currency = "€"
        break;
      default:
        i.currency = ""
        break;
    }
    const plan = (i?.recurring?.interval == 'annual' && count === 12) ? 'annual': ((i?.recurring?.interval == "month" && count == 3) ? `quarterly` : i?.recurring?.interval + 'ly');
    let dynamicButton = document.createElement("div");//js-loading-spinner
    dynamicButton.id = "myButton";
    dynamicButton.innerHTML = `<div class="options-subscription-types-element${selectedPlan && i.id === selectedPlan.id ? ' active' : ''}">
  <div class="options-subscription-types-element-label">
    <h5 class="plan-heading">${plan}</h5>
    <div class="plan-text">${i.currency}${(i.unit_amount_old ? (i.unit_amount_old / 100 / +count) : (i.unit_amount / 100 / +count)).toFixed(2)} per ${i?.recurring?.interval == "day" ? "day" : "month"}</div>
    <div class="plan-text">${i.currency}${((i.unit_amount_old) ? (i.unit_amount_old / 100) : (i.unit_amount / 100)).toFixed(2)} billed ${(i?.recurring?.interval == "month" && count == 3) ? `quarter` : i?.recurring?.interval}ly</div>
  </div>
  <button class="purchase-subscription-pay-card-btn-select js-loading-spinner" type="Submit" data-loading-finished="Select" >
    Select
  </button>
  <div class="purchase-subscription-types-element-selected">
    <img src="/assets/img/tickmark.png" alt="Tickmark" loading="lazy" class="tickmark">
  </div>
  <div class="best-value"><img src="/assets/svg/medal.svg" alt="Medal" loading="lazy" class="medal">Best value</div>
</div>`;
    dynamicButton.onclick = function () {
      selectedPlan = {
        id: i.id,
      }
      container.querySelectorAll('.options-subscription-types-element').forEach(el => {
        if (el.parentElement !== dynamicButton) {
          el.classList.remove('active');
        } else {
          el.classList.add('active');
        }
      })
      if (!payment.form.querySelector('.js-card-element').classList.contains('StripeElement--empty')) {
        payment.purchaseSubmit.disabled = false;
      }
      clickOnSubscriptionPlan(i.id, i.currency, i.unit_amount / 100, { count: count, name: i?.recurring?.interval }, freeTrialInDays, i.unit_amount_old / 100, i.discount_expires_in, { unitAmount: i.currency_options['gbp'].unit_amount, unitAmountOld: i.currency_options['gbp'].unit_amount_old });
      // document.querySelectorAll('.options-wrapper').forEach(el =>
      //   el.addEventListener('click', event => {
      //     startLoading(null, event.target);
      //   }));
    };
    if (selectedPlan && selectedPlan.id === i.id) {
      dynamicButton.dispatchEvent(new Event('click'));
    }
    
    container.appendChild(dynamicButton);
  })

  if (giftCode) {
    document.querySelector('#discount-checkout-code-input').value = giftCode;
  }

  // payment.discountCode = giftCode

  // TODO:
  // if (payment.discountCode) {
  //   const redeemWrapper = document.querySelector('.redeem-wrapper');
  //   redeemWrapper.innerHTML = `Promo applied: ${payment.discountCode}`
  // }
}

const initialDataPurchaseSubscription = () => {
  // NOTE: initial form setup
  const purchaseSubscriptionForm = document.getElementById('purchase-subscription-form');
  
  if (course) {
    purchaseSubscriptionForm.querySelector("input[name='product_id']").value = course.id;
    purchaseSubscriptionForm.querySelector("input[name='product_slug']").value = course.slug;
    purchaseSubscriptionForm.querySelector("input[name='product_type']").value = course.type;

    const priceInput = purchaseSubscriptionForm.querySelector(".js-price-input");
    priceInput.setAttribute('data-product-id', course.id);
    priceInput.setAttribute('data-product-type', course.type);
    priceInput.dataset['eur'] = course.prices.EUR;
    priceInput.dataset['gbp'] = course.prices.GBP;
    priceInput.dataset['usd'] = course.prices.USD;
    priceInput.value = course.prices.USD;
  } else {
    purchaseSubscriptionForm.querySelector("input[name='product_type']").value = 'course';
    const priceInput = purchaseSubscriptionForm.querySelector(".js-price-input");
    priceInput.setAttribute('data-product-type', 'course');
    // priceInput.dataset['eur'] = course.prices.EUR;
    // priceInput.dataset['gbp'] = course.prices.GBP;
    // priceInput.dataset['usd'] = course.prices.USD;
  }

  payment.setupForm(purchaseSubscriptionForm);
};

const applyDiscount = async (event) => {
  if (event) {
    event.preventDefault();
    event.target.disabled = true;
    startLoading(null, event.target);
  }

  const discountInput = document.getElementById('discount-checkout-code-input').value.trim();
  if (!discountInput) {
    event.target.disabled = false;
    finishLoading(null, event.target);
    return;
  }

  if (!isAuthenticated) {
    Auth.openSignUpOverlay(event, { flow: 'discount' });
    return;
  }

  payment.installationDiscount(discountInput);
  const redeemWrapper = document.querySelector('.redeem-wrapper');
  let containerError = document.getElementById("discount-checkout-error-wrapper");
  containerError.innerHTML = "";
  let hasSubscriptions = false;
  let message;
  try {
    const res = await payment.getSubscriptionsStatus();
    hasSubscriptions = res.user.has_subscription;

    await payment.redeemACode(payment.discountCode);

    event.target.disabled = false;
    finishLoading(null, event.target);
    const currency = window.localStorage.getItem('currency');
    await loadPlans(currency);
  } catch (error) {
    message = error?.message || "";
    showToast(message, "error");
    if (event) {
      event.target.disabled = false;
      finishLoading(null, event.target);
    }
};
}

const clickOnSubscriptionPlan = (id, currency, price, interval, freeTrialInDays, oldPrice, discountExpires, gbpPrices) => {
  // NOTE:
  if (!isAuthenticated) {
    //      // if user is not authenticated, start sign up flow first
    Auth.openSignUpOverlay(event, { flow: 'authenticateSelection' });
    return;
  }
  payment.gbpPrices = gbpPrices;
  setupPurchaseSubscriptionForm(currency, price, interval, freeTrialInDays, oldPrice, discountExpires);
  if (payment.stripeElements === null) {
    payment.loadStripeElements();
  }
  payment.priceId = id;
}

const setupPurchaseSubscriptionForm = (currency, price, interval, freeTrialInDays, oldPrice, discountExpires) => {
  function formatNumberForDisplay(number) {
    const formattedNumber = parseFloat(number.toFixed(2));
    if (Number.isInteger(formattedNumber)) {
        return formattedNumber.toString();
    } else {
        return formattedNumber.toFixed(2);
    }
  }

  const purchaseSubscriptionForm = document.getElementById('purchase-subscription-form');
  try {
    const containerPrice = document.querySelector(".purchase-subscription__cost-summary");
    const containerHeader = document.getElementById("purchase-subscription-header");
    const containerTotalInfo = document.querySelector(".purchase-subscription__total-info");
    const containerTitle = document.getElementById("purchase-subscription-header-title");
    const containerText = document.getElementById("purchase-subscription-terms-of-service");
    const containerSubmit = document.getElementById("purchase-product-submit");
    const containerBillingInfo = document.querySelector('.purchase-subscription__billing-info');
    let dynamicPrice = document.createElement("div");
  
    const isDiscountApplied = !isNaN(oldPrice) && price !== oldPrice;
    const isFreeTrialApplied = freeTrialInDays && freeTrialInDays !== 0;
    const plan = (interval.name === 'annual' && interval.count === 12) ? 'annual': ((interval.name == "month" && interval.count == 3) ? `quarterly` : interval.name + 'ly');
    // console.log("EXPIRES", discountExpires)
    // const [year, month, day] = discountExpires.split(' ')[0].split('-');
    // let discountExpiresInfo = '';
    // Promo code CLUB - $15.00 X deleteDiscountCode()
    if (isDiscountApplied && isFreeTrialApplied)  {
      containerHeader.innerHTML = `<span class="purchase-subscription-header__bold" id="purchase-subscription-header-title">MusicGurus ${plan} subscription:<span class="cost"><span class="currency-symbol">${currency}</span><span class="amount">${oldPrice.toFixed(2)}</span></span></span>
      <span class="purchase-subscription-header__bold purchase-subscription-header__discount" id="purchase-subscription-header-discount">Promo code&nbsp;<strong>${payment.discountCode}</strong><span>- ${currency}${(oldPrice - price).toFixed(2)} + ${freeTrialInDays} DAY FREE TRIAL</span><button class="close-btn" type="button"><img src="/assets/svg/toast-close.svg" alt="Close" loading="lazy"></button></span>`;
      dynamicPrice.innerHTML = `<div class="total-label">Total: <div class="total-amount__wrapper"><span class="currency-total-symbol">${currency}</span> <span class="amount-total">0.00</span></div></div>`;
      const localDate = new Date();
      localDate.setUTCDate(localDate.getUTCDate() + freeTrialInDays);
  
      if (discountExpires && discountExpires !== 'forever') {
        const [year, month, day] = discountExpires.split(' ')[0].split('-');
        containerBillingInfo.innerHTML = `<span class="purchase-subscription__trial-info">Subscription will return to the full price at the end of the discount period on ${getDayWithOrdinal(day)} ${months[(+month) - 1]} ${year} if you do not cancel your subscription before then.</span>`;
      } else {
        containerBillingInfo.innerHTML = `<span class="purchase-subscription__trial-info">Billing of ${currency}${price.toFixed(2)} will be charged to your payment method at the end of the trial period on ${getDayWithOrdinal(localDate.getUTCDate())} ${months[localDate.getUTCMonth()]} ${localDate.getUTCFullYear()} if you do not cancel before then.</span>`;
      }
      containerBillingInfo.classList.remove('hidden');
    } else if (isDiscountApplied && !isFreeTrialApplied) {
      containerHeader.innerHTML = `<span class="purchase-subscription-header__bold" id="purchase-subscription-header-title">MusicGurus ${plan} subscription:<span class="cost"><span class="currency-symbol">${currency}</span><span class="amount">${oldPrice.toFixed(2)}</span></span></span>
      <span class="purchase-subscription-header__bold purchase-subscription-header__discount" id="purchase-subscription-header-discount">Promo code&nbsp;<strong>${payment.discountCode}</strong><span>- ${currency}${(oldPrice - price).toFixed(2)}</span><button class="close-btn" type="button"><img src="/assets/svg/toast-close.svg" alt="Close" loading="lazy"></button></span>`;
      dynamicPrice.innerHTML = `<div class="total-label">Total: <div class="total-amount__wrapper"><span class="currency-total-symbol">${currency}</span> <span class="amount-total">${price.toFixed(2)}</span></div></div>`;
  
      if (discountExpires && discountExpires !== 'forever') {
        const [year, month, day] = discountExpires.split(' ')[0].split('-');
        containerBillingInfo.innerHTML = `<span class="purchase-subscription__trial-info">Subscription will return to the full price at the end of the discount period on ${getDayWithOrdinal(day)} ${months[(+month) - 1]} ${year} if you do not cancel your subscription before then.</span>`;
        containerBillingInfo.classList.remove('hidden');
      }
    } else if (!isDiscountApplied && isFreeTrialApplied) {
      containerHeader.innerHTML = `<span class="purchase-subscription-header__bold" id="purchase-subscription-header-title">MusicGurus ${plan} subscription: <span class="cost"><span class="currency-symbol">${currency}</span><span class="amount">${oldPrice.toFixed(2)}</span></span></span>
      <span class="purchase-subscription-header__bold purchase-subscription-header__discount" id="purchase-subscription-header-discount">Promo code&nbsp;<strong>${payment.discountCode}</strong><span>${freeTrialInDays} DAY FREE TRIAL</span><button class="close-btn" type="button"><img src="/assets/svg/toast-close.svg" alt="Close" loading="lazy"></button></span>`;
      dynamicPrice.innerHTML = `<div class="total-label">Total: <div class="total-amount__wrapper"><span class="currency-total-symbol">${currency}</span> <span class="amount-total">0.00</span></div></div>`;
      const localDate = new Date();
      localDate.setUTCDate(localDate.getUTCDate() + freeTrialInDays);
  
      containerBillingInfo.innerHTML = `<span class="purchase-subscription__trial-info">Billing of ${currency}${oldPrice.toFixed(2)} will be charged to your payment method at the end of the trial period on ${getDayWithOrdinal(localDate.getUTCDate())} ${months[localDate.getUTCMonth()]} ${localDate.getUTCFullYear()} if you do not cancel before then.</span>`;
      containerBillingInfo.classList.remove('hidden');
    } else {
      containerTitle.innerHTML = `MusicGurus ${plan} subscription:<span class="cost"><span class="currency-symbol">${currency}</span><span class="amount">${price.toFixed(2)}</span></span>`;
      dynamicPrice.innerHTML = `<div class="total-label">Total: <div class="total-amount__wrapper"><span class="currency-total-symbol">${currency}</span> <span class="amount-total">${price.toFixed(2)}</span></div></div>`;
    }
  
    const todayDate = new Date();
    const termsOfService = document.querySelector('.purchase-subscription__terms-of-service');
    termsOfService.innerHTML = `<strong>By clicking "Place Secure Order", you agree to adhere to our <a href="/terms" class="link-decoration">Terms of Service</a> and authorize a recurring ${plan} charge of ${currency}${price.toFixed(2)}</strong> that will be automatically applied to your payment method and start on ${getDayWithOrdinal(todayDate.getUTCDate())} ${months[todayDate.getUTCMonth()]} ${todayDate.getUTCFullYear()}. All amounts paid are non-refundable, and rates for subsequent billing periods may change. Cancel your subscription at any time in user settings.`
 
    const removeBtn = containerHeader.querySelector('.close-btn');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        payment.deleteDiscountCode().then(() => {
          payment.discountCode = null;
          const currency = window.localStorage.getItem('currency');
          let currencySign = '$';
          switch (currency.toLowerCase()) {
            case "usd":
              currencySign = '$';
              break;
            case "gbp":
              currencySign = "£"
              break;
            case "eur":
              currencySign = "€"
              break;
            default:
              currencySign = ""
              break;
          }
          const header = document.querySelector('.purchase-subscription-header');
          header.innerHTML = `<span class="purchase-subscription-header__bold" id="purchase-subscription-header-title">MusicGurus ${interval.name === 'month' && interval.count === 3 ? 'quarter' : interval.name}ly subscription:<span class="cost"><span class="currency-symbol">${currencySign}</span><span class="amount">${oldPrice.toFixed(2)}</span></span></span>`;
          
          dynamicPrice.innerHTML = `<span>Total: ${currencySign}${oldPrice.toFixed(2)}</span>`;
          containerPrice.innerHTML = dynamicPrice.innerHTML;
          containerBillingInfo.innerHTML = "";
          containerBillingInfo.classList.add('hidden');
  
          loadPlans(currency); 
        })
      })
    }
    containerPrice.innerHTML = dynamicPrice.innerHTML;
  }
  catch (error) {
    console.log("error", error);
  }
}

// Uses Cloudflare GEO IP Country Code, currency
const configureCurrency = () => {
  const currency = window.localStorage.getItem('currency');
  if (currency) {
    injectCurrency(currency);

    const currencySelector = document.getElementById('currency-selector');
    if (currencySelector) {
      Array.from(currencySelector.options).forEach(option => {
        option.selected = option.value === currency;
      });
    }
  } else {
    fetchCountryCode();
  }
}

const fetchCountryCode = () => {
  // Construct an HTTP request
  const xhr = createXHR({
    method: 'GET',
    endpoint: '/api/v2/country-code'
  });
  xhr.onload = () => {
    const data = JSON.parse(xhr.responseText);
    const countryCode = Object.keys(countryCodeCurrency).indexOf(data.country_code) !== -1 ? data.country_code : 'US';
    const currency = countryCodeCurrency[countryCode];
    const supportedCurrency = supportedCurrencies.indexOf(currency) !== -1 ? currency : 'USD';
    window.localStorage.setItem('currency', supportedCurrency);
    window.localStorage.setItem('countryCode', countryCode);
    injectCurrency(supportedCurrency);
  };
}

const injectCurrency = currency => {
  const currencyLower = currency.toLowerCase();
  // clear currency classes
  document.body.classList.remove(...supportedCurrencies.map(c => `currency-${c.toLowerCase()}`));
  // add selected currency class
  document.body.classList.add(`currency-${currencyLower}`);
  // update prices with new currency
  document.querySelectorAll('.js-price').forEach(el => el.textContent = el.dataset[currency.toLowerCase()]);
  document.querySelectorAll('.js-price-input').forEach(el => el.value = el.dataset[currency.toLowerCase()]);
  // update currency form inputs
  document.querySelectorAll('input.js-currency').forEach(el => el.value = currency);
  // update currency selector
  document.querySelectorAll('#currency-selector').forEach(selector => selector.value = currency);

  // Load PayPal script
  const existingScript = document.getElementById('paypal-script');
  if (existingScript !== null && existingScript.src.indexOf(`currency=${currency}`) === -1) {
    // remove current paypal script first
    document.getElementById('paypal-script').remove();
    // remove rendered paypal buttons
    payment.paypal.button = null;
    gift.paypal.button = null;
    document.querySelectorAll('.js-paypal-btn-element').forEach(el => el.innerHTML = '');
  }

  // inject paypal script
  /*const scriptEl = document.createElement('script');
  scriptEl.id = 'paypal-script';
  scriptEl.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&currency=${currency}&intent=capture`;
  scriptEl.onload = scriptEl.onreadystatechange = () => {
    payment.initPayPal();
    gift.initPayPal();
  }
  document.head.appendChild(scriptEl);*/
}

const parserSecretId = (id) => {
  if (id.includes("pi")) return "pi";
  if (id.includes("seti")) return "seti";
  return 0;
}

function getCurrencySign (currency) {
  switch (currency.toLowerCase()) {
    case "usd":
      return '$';
    case "gbp":
      return "£";
    case "eur":
      return "€";
    default:
      return "";
  }
}
document.addEventListener("DOMContentLoaded", () => {
  const targetNode = document.querySelector('.js-card-element');
  const stripeClick = document.getElementById('stripe-click');
  const authBtn = document.querySelector('.c-auth__btn.js-payment-request-btn-element');

  if (targetNode && stripeClick) {
    new MutationObserver(([mutation]) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class' &&
          mutation.target.classList.contains('StripeElement--empty') && 
          mutation.target.classList.contains('StripeElement--focus')) {
        stripeClick.textContent = 'true';
      }
    }).observe(targetNode, { attributes: true, attributeFilter: ['class'] });
  }


  if (authBtn && stripeClick) {
    new MutationObserver(([mutation]) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        if (mutation.target.classList.contains('StripeElement--focus')) {
          stripeClick.textContent = 'true';
        }
      }
    }).observe(authBtn, { attributes: true, attributeFilter: ['class'] });
  }
});

export default {init};
